import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtMoney, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Plus, Check, Wallet, ArrowDownCircle, ArrowUpCircle, CalendarDays, ArrowRight,
  Tags, Pencil, Trash2, Paperclip, Upload, FileText, Camera,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";

const KpiBox = ({ label, value, accent, icon }) => (
  <div className="card-soft p-5">
    {icon && <div className="w-10 h-10 rounded-xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center mb-3">{icon}</div>}
    <div className="text-xs text-[var(--text-2)] font-medium">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${accent || ""}`}>{value}</div>
  </div>
);

const TABS = [
  { key: "overview",     label: "Visão Geral",       icon: Wallet },
  { key: "receivable",   label: "Contas a Receber",  icon: ArrowDownCircle },
  { key: "payable",      label: "Contas a Pagar",    icon: ArrowUpCircle },
  { key: "categories",   label: "Categorias",        icon: Tags },
];

function defaultCustomRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

export default function Financial() {
  const { t } = useI18n();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [overviewPeriod, setOverviewPeriod] = useState("semester");
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/categories");
      setCategories(data || []);
    } catch {
      setCategories([]);
    }
  }, []);

  const loadSummary = useCallback(async (period = overviewPeriod, range = customRange) => {
    let url = `/finance/summary?period=${period}`;
    if (period === "custom") {
      url += `&from=${range.from}&to=${range.to}`;
    }
    const { data } = await api.get(url);
    setSummary(data);
  }, [overviewPeriod, customRange]);

  useEffect(() => {
    void loadSummary();
    void loadCategories();
  }, [loadSummary, loadCategories]);

  const handleOverviewPeriodChange = (period) => {
    setOverviewPeriod(period);
    void loadSummary(period, customRange);
  };

  const handleCustomRangeChange = (nextRange) => {
    setCustomRange(nextRange);
    if (overviewPeriod === "custom") {
      void loadSummary("custom", nextRange);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-[var(--text-2)] font-medium">Financeiro</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">{t("nav_financial")}</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-2xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4" data-testid="new-finance-entry">
          <Plus size={14} className="mr-1" /> {t("add_entry")}
        </Button>
      </div>

      {/* KPI row (visible on all tabs) */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiBox label={t("finance_balance")} value={fmtMoney(summary.balance_realized)}
                  accent="text-[var(--success-text)]" icon={<Wallet size={18} />} />
          <KpiBox label={`${t("receivable")} · ${t("pending")}`} value={fmtMoney(summary.totals.receivable_pending)}
                  icon={<ArrowDownCircle size={18} />} />
          <KpiBox label={`${t("payable")} · ${t("pending")}`} value={fmtMoney(summary.totals.payable_pending)}
                  accent="text-[var(--danger)]" icon={<ArrowUpCircle size={18} />} />
        </div>
      )}

      {/* Tabs */}
      <div className="card-soft p-1.5 flex items-center gap-1 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 h-10 px-3 rounded-2xl text-xs font-semibold whitespace-nowrap transition-colors ${
              tab === key
                ? "bg-[var(--brand)] text-white shadow-sm"
                : "text-[var(--text-2)] hover:bg-[var(--surface-2)]"
            }`}
            data-testid={`fin-tab-${key}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Tab bodies */}
      {tab === "overview" && summary && (
        <OverviewTab
          summary={summary}
          period={overviewPeriod}
          customRange={customRange}
          onPeriodChange={handleOverviewPeriodChange}
          onCustomRangeChange={handleCustomRangeChange}
        />
      )}
      {(tab === "receivable" || tab === "payable") && (
        <EntriesTab kind={tab} onChange={loadSummary} />
      )}
      {tab === "categories" && (
        <CategoriesTab categories={categories} onChange={loadCategories} />
      )}

      <NewEntryDialog
        open={open}
        onOpenChange={setOpen}
        categories={categories}
        onSaved={() => { loadSummary(); loadCategories(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview — monthly cashflow bars
// ---------------------------------------------------------------------------
const OVERVIEW_PERIOD_OPTIONS = [
  { key: "month", label: "Mês" },
  { key: "semester", label: "Semestre" },
  { key: "year", label: "Ano" },
  { key: "custom", label: "Personalizado" },
];

const OVERVIEW_PERIOD_TITLES = {
  month: "Fluxo de caixa — mês atual",
  semester: "Fluxo de caixa — semestre",
  year: `Fluxo de caixa — ${new Date().getFullYear()}`,
  custom: "Fluxo de caixa — período personalizado",
};

const MONTH_OPTIONS = [
  { value: "01", label: "Jan" }, { value: "02", label: "Fev" }, { value: "03", label: "Mar" },
  { value: "04", label: "Abr" }, { value: "05", label: "Mai" }, { value: "06", label: "Jun" },
  { value: "07", label: "Jul" }, { value: "08", label: "Ago" }, { value: "09", label: "Set" },
  { value: "10", label: "Out" }, { value: "11", label: "Nov" }, { value: "12", label: "Dez" },
];

const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => {
  const year = new Date().getFullYear() - 8 + i;
  return { value: String(year), label: String(year) };
});

function parseMonthYear(isoDate) {
  const [year, month] = isoDate.slice(0, 7).split("-");
  return { year, month };
}

function monthStart(year, month) {
  return `${year}-${month}-01`;
}

function monthEnd(year, month) {
  const lastDay = new Date(Number(year), Number(month), 0).getDate();
  return `${year}-${month}-${String(lastDay).padStart(2, "0")}`;
}

function MonthYearPicker({ value, onChange, testIdPrefix }) {
  const { year, month } = parseMonthYear(value);

  const update = (nextYear, nextMonth) => {
    onChange({ year: nextYear, month: nextMonth });
  };

  return (
    <div className="flex items-center gap-1.5">
      <Select value={month} onValueChange={(nextMonth) => update(year, nextMonth)}>
        <SelectTrigger className="rounded-xl h-9 w-[72px] bg-white border-[var(--border-c)] text-xs font-semibold" data-testid={`${testIdPrefix}-month`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTH_OPTIONS.map((m) => (
            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={year} onValueChange={(nextYear) => update(nextYear, month)}>
        <SelectTrigger className="rounded-xl h-9 w-[84px] bg-white border-[var(--border-c)] text-xs font-semibold" data-testid={`${testIdPrefix}-year`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {YEAR_OPTIONS.map((y) => (
            <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function OverviewTab({ summary, period, customRange, onPeriodChange, onCustomRangeChange }) {
  const hasData = summary.monthly?.some((row) =>
    row.revenue > 0 || row.expense > 0 || row.revenue_pending > 0 || row.expense_pending > 0
  );
  const title = period === "custom" && summary.range_label
    ? `Fluxo de caixa — ${summary.range_label}`
    : OVERVIEW_PERIOD_TITLES[period];
  const manyBuckets = (summary.monthly?.length || 0) > 12;

  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <div className="text-xs text-[var(--text-2)] mt-0.5">
            Por mês de vencimento — barras sólidas: pagos · barras claras: previstos
          </div>
        </div>
        <div className="card-soft p-1 flex items-center gap-1 overflow-x-auto shrink-0">
          {OVERVIEW_PERIOD_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => onPeriodChange(key)}
              className={`h-9 px-3 rounded-2xl text-xs font-semibold whitespace-nowrap transition-colors ${
                period === key
                  ? "bg-[var(--brand)] text-white shadow-sm"
                  : "text-[var(--text-2)] hover:bg-[var(--surface-2)]"
              }`}
              data-testid={`overview-period-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <div className="rounded-2xl bg-[var(--surface-2)] border border-[var(--border-c)] px-4 py-3 mb-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-2)] shrink-0">
            <div className="w-8 h-8 rounded-xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center">
              <CalendarDays size={15} />
            </div>
            Intervalo
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide font-semibold text-[var(--text-3)]">De</span>
            <MonthYearPicker
              value={customRange.from}
              testIdPrefix="overview-custom-from"
              onChange={({ year, month }) => onCustomRangeChange({ ...customRange, from: monthStart(year, month) })}
            />
            <ArrowRight size={14} className="text-[var(--text-3)] mx-0.5" />
            <span className="text-[10px] uppercase tracking-wide font-semibold text-[var(--text-3)]">Até</span>
            <MonthYearPicker
              value={customRange.to}
              testIdPrefix="overview-custom-to"
              onChange={({ year, month }) => onCustomRangeChange({ ...customRange, to: monthEnd(year, month) })}
            />
          </div>

          {summary.range_label && (
            <span className="pill pill-info ml-auto font-mono text-[11px]">{summary.range_label}</span>
          )}
        </div>
      )}

      {!hasData ? (
        <div className="text-center py-16 text-[var(--text-2)] text-sm">
          Nenhuma receita ou despesa no período selecionado.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={manyBuckets ? 320 : 280}>
          <BarChart data={summary.monthly} margin={manyBuckets ? { bottom: 20 } : undefined}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="month"
              stroke="hsl(var(--muted-foreground))"
              fontSize={10}
              angle={manyBuckets ? -35 : 0}
              textAnchor={manyBuckets ? "end" : "middle"}
              height={manyBuckets ? 60 : 30}
            />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} formatter={(v) => fmtMoney(v)} />
            <Legend />
            <Bar dataKey="revenue" name="Receitas (pagas)" stackId="revenue" fill="#43E695" radius={[0, 0, 0, 0]} />
            <Bar dataKey="revenue_pending" name="Receitas (previstas)" stackId="revenue" fill="#43E695" fillOpacity={0.35} radius={[6, 6, 0, 0]} />
            <Bar dataKey="expense" name="Despesas (pagas)" stackId="expense" fill="#EF4444" radius={[0, 0, 0, 0]} />
            <Bar dataKey="expense_pending" name="Despesas (previstas)" stackId="expense" fill="#EF4444" fillOpacity={0.35} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AR / AP list
// ---------------------------------------------------------------------------
function EntriesTab({ kind, onChange }) {
  const { t } = useI18n();
  const partyLabel = kind === "receivable" ? t("finance_party_receivable") : t("finance_party_payable");
  const [entries, setEntries] = useState([]);
  const [attachCounts, setAttachCounts] = useState({});
  const [attachEntry, setAttachEntry] = useState(null);

  const load = useCallback(async () => {
    const [{ data }, { data: atts }] = await Promise.all([
      api.get(`/finance/entries?kind=${kind}`),
      api.get("/evidences?finance=1").catch(() => ({ data: [] })),
    ]);
    setEntries(data);
    const counts = {};
    (atts || []).forEach((a) => {
      if (!a.finance_entry_id) return;
      counts[a.finance_entry_id] = (counts[a.finance_entry_id] || 0) + 1;
    });
    setAttachCounts(counts);
  }, [kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const markPaid = async (id) => {
    await api.patch(`/finance/entries/${id}`, { status: "paid", paid_at: new Date().toISOString() });
    toast.success("Baixado");
    load(); onChange && onChange();
  };

  // Group installments by parent_id for cleaner display
  const grouped = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const key = e.parent_id || e.id;
      const g = map.get(key) || { key, items: [], total: 0, paid: 0 };
      g.items.push(e);
      g.total += e.amount;
      if (e.status === "paid") g.paid += e.amount;
      map.set(key, g);
    });
    return Array.from(map.values());
  }, [entries]);

  return (
    <div className="card-soft overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)] border-b border-[var(--border-c)]">
            <tr className="text-left text-xs text-[var(--text-2)] font-semibold">
              <th className="px-4 py-3">{t("description")}</th>
              <th className="px-4 py-3">Categoria</th>
              <th className="px-4 py-3">{partyLabel}</th>
              <th className="px-4 py-3">{t("due_date")}</th>
              <th className="px-4 py-3">Parcela</th>
              <th className="px-4 py-3 text-right">{t("amount")}</th>
              <th className="px-4 py-3">{t("status")}</th>
              <th className="px-4 py-3 text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[var(--text-2)]">{t("no_data")}</td></tr>}
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-[var(--border-c)] hover:bg-[var(--surface-2)]" data-testid={`finance-row-${e.id}`}>
                <td className="px-4 py-3 font-medium">{e.description}</td>
                <td className="px-4 py-3 text-[var(--text-2)]">{e.category || "—"}</td>
                <td className="px-4 py-3 text-[var(--text-2)]">{e.party || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{e.due_date}</td>
                <td className="px-4 py-3 text-xs">
                  {e.installments_total > 1
                    ? <span className="pill pill-info">{e.installment_no}/{e.installments_total}</span>
                    : <span className="text-[var(--text-3)]">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-mono">{fmtMoney(e.amount)}</td>
                <td className="px-4 py-3"><span className={`pill ${e.status === "paid" ? "pill-success" : e.status === "cancelled" ? "pill-neutral" : "pill-warning"}`}>{t(e.status)}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setAttachEntry(e)}
                      className="inline-flex items-center gap-1 px-2.5 h-8 rounded-xl bg-[var(--surface-2)] text-xs font-semibold hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]"
                      data-testid={`finance-attach-${e.id}`}
                    >
                      <Paperclip size={12} />
                      {attachCounts[e.id] ? attachCounts[e.id] : "Anexar"}
                    </button>
                    {e.status !== "paid" && (
                      <button onClick={() => markPaid(e.id)} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-xl bg-[var(--surface-2)] text-xs font-semibold hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]" data-testid={`mark-paid-${e.id}`}>
                        <Check size={12} /> {t("mark_paid")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {grouped.some((g) => g.items.length > 1) && (
        <div className="border-t border-[var(--border-c)] px-4 py-2 bg-[var(--surface-2)] text-[10px] text-[var(--text-2)]">
          Contratos com múltiplas parcelas aparecem agrupados pela mesma descrição base.
        </div>
      )}
      <EntryAttachmentsDialog
        entry={attachEntry}
        onOpenChange={(open) => { if (!open) setAttachEntry(null); }}
        onChanged={load}
      />
    </div>
  );
}

async function openFinanceFile(id) {
  const { data } = await api.get(`/evidences/${id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function EntryAttachmentsDialog({ entry, onOpenChange, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const load = useCallback(async () => {
    if (!entry?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/evidences?finance_entry_id=${entry.id}`);
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [entry?.id]);

  useEffect(() => {
    if (entry?.id) void load();
    else { setItems([]); setFile(null); setDeleteItem(null); }
  }, [entry?.id, load]);

  const onUpload = async () => {
    if (!file || !entry?.id) return;
    setUploading(true);
    try {
      const params = new URLSearchParams({ finance_entry_id: entry.id });
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/evidences/upload?${params}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Arquivo anexado");
      setFile(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Falha ao anexar arquivo");
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/evidences/${deleteItem.id}`);
      toast.success("Anexo removido");
      setDeleteItem(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Falha ao remover anexo");
      throw err;
    }
  };

  return (
    <>
    <Dialog open={!!entry} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="finance-attach-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold">Anexos do lançamento</DialogTitle>
        </DialogHeader>
        {entry && (
          <p className="text-xs text-[var(--text-2)] -mt-1">{entry.description}</p>
        )}

        <div className="rounded-2xl border border-dashed border-[var(--border-c)] p-4 space-y-3">
          <div className="text-sm font-semibold">Adicionar PDF ou foto</div>
          <label className="flex items-center justify-center gap-2 h-10 rounded-xl border border-[var(--border-c)] bg-[var(--surface)] cursor-pointer hover:bg-[var(--surface-2)] text-xs font-medium">
            <Paperclip size={14} />
            {file ? file.name : "Selecionar arquivo"}
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="finance-attach-file"
            />
          </label>
          <Button
            type="button"
            onClick={onUpload}
            disabled={!file || uploading}
            className="rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold w-full"
            data-testid="finance-attach-upload"
          >
            <Upload size={14} className="mr-1.5" />
            {uploading ? "Enviando..." : "Enviar anexo"}
          </Button>
        </div>

        {loading && <div className="text-sm text-[var(--text-2)]">Carregando anexos...</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm text-[var(--text-2)]">Nenhum arquivo neste lançamento.</div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto">
            {items.map((e) => {
              const isImg = e.content_type?.startsWith("image/");
              return (
                <div
                  key={e.id}
                  className="rounded-2xl border border-[var(--border-c)] p-2.5 space-y-2"
                  data-testid={`finance-attachment-${e.id}`}
                >
                  <button
                    type="button"
                    onClick={() => openFinanceFile(e.id).catch(() => toast.error("Falha ao abrir arquivo"))}
                    className="w-full rounded-xl bg-[var(--surface-2)] aspect-[4/3] flex items-center justify-center hover:opacity-90"
                  >
                    {isImg ? <Camera size={28} className="text-[var(--brand)]" /> : <FileText size={28} className="text-[var(--brand)]" />}
                  </button>
                  <div className="text-xs min-w-0">
                    <div className="font-medium truncate">{e.original_filename || "Arquivo"}</div>
                    <div className="text-[var(--text-3)]">{fmtDate(e.created_at)}</div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="w-full h-9 text-destructive hover:text-destructive"
                    onClick={() => setDeleteItem(e)}
                    data-testid={`finance-attachment-delete-${e.id}`}
                  >
                    <Trash2 size={14} className="mr-1" /> Excluir
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>

    <ConfirmDeleteDialog
      open={!!deleteItem}
      onOpenChange={(open) => { if (!open) setDeleteItem(null); }}
      title="Excluir anexo?"
      itemName={deleteItem ? `"${deleteItem.original_filename || "Arquivo"}"` : null}
      description="O arquivo sai deste lançamento e não poderá ser recuperado por esta tela."
      onConfirm={confirmDelete}
      testId="finance-attach-confirm-delete"
    />
    </>
  );
}

// ---------------------------------------------------------------------------
// New entry dialog — with installments
// ---------------------------------------------------------------------------
function NewEntryDialog({ open, onOpenChange, onSaved, categories = [] }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    kind: "receivable", description: "", amount: "", due_date: "",
    party: "", category: "", installments: 1,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/finance/entries", {
        ...form,
        amount: parseFloat(form.amount),
        installments: parseInt(form.installments) || 1,
      });
      toast.success(form.installments > 1 ? `${form.installments} parcelas criadas` : "Lançamento criado");
      onSaved();
      onOpenChange(false);
      setForm({ kind: "receivable", description: "", amount: "", due_date: "", party: "", category: "", installments: 1 });
    } catch { toast.error("Falha"); } finally { setBusy(false); }
  };

  const perInstallment = form.amount && form.installments > 1
    ? (parseFloat(form.amount) / parseInt(form.installments)).toFixed(2)
    : null;
  const partyLabel = form.kind === "receivable" ? t("finance_party_receivable") : t("finance_party_payable");
  const partyHint = form.kind === "receivable" ? t("finance_party_hint_receivable") : t("finance_party_hint_payable");
  const partyPlaceholder = form.kind === "receivable" ? "Ex.: Cartório Central" : "Ex.: Fornecedor XYZ";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="finance-dialog">
        <DialogHeader><DialogTitle className="font-display font-bold">{t("add_entry")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[var(--text-2)]">{t("kind")}</label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="finance-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="receivable">{t("receivable")}</SelectItem>
                <SelectItem value="payable">{t("payable")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--text-2)]">{t("description")}</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl h-10 mt-1" data-testid="finance-input-description" required />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-[var(--text-2)]">{partyLabel}</label>
              <Input
                value={form.party}
                onChange={(e) => setForm({ ...form, party: e.target.value })}
                className="rounded-xl h-10 mt-1"
                placeholder={partyPlaceholder}
                data-testid="finance-input-party"
              />
              <p className="text-[10px] text-[var(--text-3)] mt-1">{partyHint}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--text-2)]">Categoria</label>
              <Select
                value={form.category || "__none__"}
                onValueChange={(v) => setForm({ ...form, category: v === "__none__" ? "" : v })}
              >
                <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="finance-input-category">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem categoria</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-[var(--text-2)]">Valor total</label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl h-10 mt-1" data-testid="finance-input-amount" required />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--text-2)]">1º vencimento</label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-xl h-10 mt-1" data-testid="finance-input-due_date" required />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--text-2)]">Parcelas (mensais)</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min="1" max="60"
                value={form.installments}
                onChange={(e) => setForm({ ...form, installments: e.target.value })}
                className="rounded-xl h-10 w-24"
                data-testid="finance-input-installments"
              />
              <div className="flex gap-1">
                {[1, 2, 3, 6, 12].map((n) => (
                  <button
                    key={n} type="button"
                    onClick={() => setForm({ ...form, installments: n })}
                    className={`px-2.5 h-8 rounded-lg text-xs font-semibold transition-colors ${
                      parseInt(form.installments) === n
                        ? "bg-[var(--brand)] text-white"
                        : "bg-[var(--surface-2)] text-[var(--text-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]"
                    }`}
                    data-testid={`preset-installments-${n}`}
                  >{n}x</button>
                ))}
              </div>
            </div>
            {perInstallment && (
              <div className="mt-2 text-xs text-[var(--text-2)]">
                <span className="font-semibold text-[var(--brand)]">{form.installments}x</span> de <span className="font-mono">R$ {perInstallment}</span>
                <span className="block text-[10px] text-[var(--text-3)] mt-0.5">{t("finance_installments_hint")}</span>
              </div>
            )}
          </div>

          <Button type="submit" disabled={busy} className="w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11" data-testid="finance-submit">
            {busy ? "Salvando..." : t("save")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CategoriesTab({ categories, onChange }) {
  const [name, setName] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await api.post("/finance/categories", { name: trimmed });
      toast.success("Categoria adicionada");
      setName("");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao adicionar categoria");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editItem) return;
    const trimmed = editName.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.patch("/finance/categories", { from: editItem.name, to: trimmed });
      toast.success("Categoria atualizada");
      setEditItem(null);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao atualizar categoria");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/finance/categories?name=${encodeURIComponent(deleteItem.name)}`);
      toast.success("Categoria excluída");
      setDeleteItem(null);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir categoria");
      throw err;
    }
  };

  return (
    <div className="space-y-3">
      <div className="card-soft p-4 space-y-3">
        <div>
          <h2 className="font-semibold text-sm">Nova categoria</h2>
          <p className="text-xs text-[var(--text-2)] mt-0.5">
            Use a lista no lançamento de contas a pagar e a receber.
          </p>
        </div>
        <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Marketing"
            className="rounded-xl h-10"
            data-testid="finance-category-name"
          />
          <Button type="submit" className="h-10 rounded-xl" data-testid="finance-category-add">
            <Plus size={14} className="mr-1" /> Adicionar
          </Button>
        </form>
      </div>

      <div className="grid gap-2">
        {categories.length === 0 && (
          <div className="card-soft p-8 text-center text-sm text-[var(--text-2)]">Nenhuma categoria ainda.</div>
        )}
        {categories.map((c) => (
          <div key={c.name} className="card-soft p-3 flex flex-wrap justify-between items-center gap-3 text-sm" data-testid={`finance-category-${c.name}`}>
            <div className="min-w-0">
              <div className="font-semibold">{c.name}</div>
              <div className="text-xs text-[var(--text-2)]">
                {c.count} lançamento(s)
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setEditItem(c); setEditName(c.name); }}
                data-testid={`finance-category-edit-${c.name}`}
              >
                <Pencil size={14} className="mr-1" /> Editar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteItem(c)}
                data-testid={`finance-category-delete-${c.name}`}
              >
                <Trash2 size={14} className="mr-1" /> Excluir
              </Button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDeleteDialog
        open={!!deleteItem}
        onOpenChange={(open) => { if (!open) setDeleteItem(null); }}
        title="Excluir categoria?"
        itemName={deleteItem ? `"${deleteItem.name}"` : null}
        description="A categoria sai da lista. Lançamentos que a usavam ficam sem categoria."
        onConfirm={confirmDelete}
        testId="finance-category-confirm-delete"
      />

      <Dialog open={!!editItem} onOpenChange={(open) => { if (!open) setEditItem(null); }}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="finance-category-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">Editar categoria</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="rounded-xl h-10"
              data-testid="finance-category-edit-name"
              required
            />
            <Button type="submit" disabled={busy} className="w-full rounded-xl h-11" data-testid="finance-category-edit-submit">
              {busy ? "Salvando..." : "Salvar alterações"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
